/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    for (const entry of entries ?? []) {
      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        id: entry.id,
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

function parseSection(html) {
  return new DOMParser().parseFromString(html, 'text/html');
}

async function fetchRenderedSections(sectionIds) {
  if (!sectionIds.length) return {};

  // Shopify's Section Rendering API accepts at most five section IDs per
  // request. Preserve preview/locale query parameters while adding sections.
  const batches = [];
  for (let index = 0; index < sectionIds.length; index += 5) {
    batches.push(sectionIds.slice(index, index + 5));
  }

  const responses = await Promise.all(
    batches.map(async (batch) => {
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set('sections', batch.join(','));
      const response = await fetch(url, { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Section Rendering request failed (${response.status})`);
      return response.json();
    }),
  );

  return Object.assign({}, ...responses);
}

function replaceCartDrawer(html) {
  const drawer = document.querySelector('cart-drawer');
  const freshDrawer = parseSection(html).querySelector('cart-drawer');
  const currentContent = drawer?.querySelector('#CartDrawer');
  const freshContent = freshDrawer?.querySelector('#CartDrawer');
  if (!drawer || !freshDrawer || !currentContent || !freshContent) return false;

  const wasOpen = drawer.classList.contains('active');
  const focusedId = drawer.contains(document.activeElement) ? document.activeElement.id : '';

  // Preserve the custom-element root: its constructor owns the persistent
  // header-cart listener. Replacing only its contents avoids leaked listeners.
  currentContent.replaceChildren(...freshContent.childNodes);
  drawer.classList.toggle('is-empty', freshDrawer.classList.contains('is-empty'));
  drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));

  if (wasOpen) {
    drawer.classList.add('active', 'animate');
    document.body.classList.add('overflow-hidden');

    const noteSummary = drawer.querySelector('[id^="Details-"] summary');
    if (noteSummary && !noteSummary.hasAttribute('role')) drawer.setSummaryAccessibility(noteSummary);

    const focusContainer = drawer.classList.contains('is-empty')
      ? drawer.querySelector('.drawer__inner-empty')
      : drawer.querySelector('#CartDrawer');
    const restoredFocus = focusedId ? document.getElementById(focusedId) : null;
    const focusTarget = restoredFocus && drawer.contains(restoredFocus)
      ? restoredFocus
      : drawer.querySelector('.drawer__inner');
    if (focusContainer && focusTarget) trapFocus(focusContainer, focusTarget);
  }

  return true;
}

function replaceRenderedSection(sectionId, entry, html) {
  const parsed = parseSection(html);

  if (sectionId === 'cart-drawer' && replaceCartDrawer(html)) return;

  if (entry.id === 'main-cart-items') {
    const current = document.querySelector('cart-items');
    const source = parsed.querySelector('cart-items');
    if (current && source) current.replaceWith(source);
    return;
  }

  if (entry.id === 'main-cart-footer') {
    const current = document.getElementById('main-cart-footer');
    const source = parsed.getElementById('main-cart-footer');
    if (current && source) current.replaceWith(source);
    return;
  }

  const source = parsed.querySelector(entry.extractSelector);
  const mount = entry.mount.isConnected ? entry.mount : document.getElementById(entry.id);
  if (source && mount) mount.replaceChildren(...source.childNodes);
}

// After a Storefront API mutation, fetch the Ajax cart shape needed by Dawn's
// subscribers and independently fetch fresh Section Rendering HTML. Replacing
// the complete drawer root keeps empty/non-empty state and focus behavior in
// sync, including Standard Action transitions between zero and one items.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const [cartResponse, renderedSections] = await Promise.all([
    fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }),
    fetchRenderedSections([...sections.keys()]),
  ]);
  if (!cartResponse.ok) throw new Error(`Ajax cart request failed (${cartResponse.status})`);
  const cartData = await cartResponse.json();
  cartData.sections = renderedSections;

  for (const [sectionId, entry] of sections) {
    const html = renderedSections[sectionId];
    if (html) replaceRenderedSection(sectionId, entry, html);
  }

  // Hand off to Dawn's internal subscribers and wait for their async work so
  // `await updateCart(); await openCart()` never opens stale cart UI.
  await publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    eventTarget: (meta = {}) => {
      const onCartPage = document.body?.classList.contains('template-cart');
      const cartContext = onCartPage
        ? document.querySelector('cart-items')
        : document.querySelector('cart-drawer-items');

      if (meta.type === 'shopify:cart:note-update') {
        return (onCartPage
          ? document.querySelector('#main-cart-footer cart-note')
          : document.querySelector('cart-drawer cart-note')) || cartContext || document;
      }

      if (meta.type === 'shopify:cart:discount-update') {
        return document.querySelector('cart-discount') || cartContext || document;
      }

      if (meta.type === 'shopify:cart:lines-update' && meta.action === 'add') {
        return document.querySelector('product-form') || cartContext || document;
      }

      return cartContext || document.querySelector('cart-drawer') || document;
    },
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}
